import { observer } from 'mobx-react-lite'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { type JobActionsViewModel } from '@/features/jobs/details/JobActionsViewModel'
import { getStatusDisplayText } from '@/features/jobs/job-status-display'
import { JobActionsMenuItems } from '@/features/jobs/details/components/JobActionsMenuItems'
import {
  Actions,
  ConsultationSummary,
  HeaderView,
  JobHeaderInfo,
  NotesSituation,
  PayoutSituation,
  StatusHistorySummary,
} from '@/features/jobs/details/components/HeaderView'

export interface HeaderViewContainerProps {
  vm: JobDetailsViewModel
  onJobListSidebarToggle?: () => unknown
  onJobListHoverChange: (isHovering: boolean) => unknown
  isJobListSidebarVisible: boolean
}

export const HeaderViewContainer = observer(function HeaderViewContainer({
  vm,
  onJobListSidebarToggle,
  onJobListHoverChange,
  isJobListSidebarVisible,
}: HeaderViewContainerProps) {
  return (
    <HeaderView
      onJobListSidebarToggle={onJobListSidebarToggle}
      onJobListHoverChange={onJobListHoverChange}
      isJobListSidebarVisible={isJobListSidebarVisible}
      jobInfo={<JobHeaderInfoContainer vm={vm} />}
      actions={<ActionsContainer vm={vm.actionsVm} />}
      showTabs={vm.job.status !== 'UnderConstruction'}
      currentTab={vm.currentTab}
      onTabChange={vm.setTab}
      communicationUnreadCount={vm.conversationVm.unreadCount}
    />
  )
})

interface JobHeaderInfoContainerProps {
  vm: JobDetailsViewModel
}

const JobHeaderInfoContainer = observer(function JobHeaderInfoContainer({
  vm,
}: JobHeaderInfoContainerProps) {
  const statusHistorySummary = vm.statusHistorySummary
  const consultation = vm.job.consultation
  return (
    <JobHeaderInfo
      jobName={vm.job.displayName}
      isDraftOrPending={vm.job.isDraft || vm.job.isPending}
      formattedStatus={getStatusDisplayText(vm.job.status)}
      advanceAllowedDate={vm.actionsVm.advanceAllowedDate}
      deadline={vm.job.dateDeadline}
      daysLeft={vm.daysLeft}
    >
      {vm.billingVm.canViewPayoutSplit && (
        <PayoutSituation situationType={vm.job.payoutSplit?.situation.type} />
      )}
      {statusHistorySummary !== null && (
        <StatusHistorySummary
          prefix={statusHistorySummary.prefix}
          time={statusHistorySummary.time}
          suffix={statusHistorySummary.suffix}
        />
      )}
      {consultation && !consultation.skipScheduling && (
        <ConsultationSummary
          isPendingScheduling={
            vm.consultationVm.consultationIsPendingScheduling
          }
          pendingSchedulingText={
            vm.consultationVm.consultationPendingSchedulingText
          }
          bookedStartTime={consultation.bookedTime?.startTime}
        />
      )}
      {vm.capturedInformationVm.notesVm.areNotesEnabled && <NotesSituation />}
    </JobHeaderInfo>
  )
})

interface ActionsContainerProps {
  vm: JobActionsViewModel
}

const ActionsContainer = observer(function ActionsContainer({
  vm,
}: ActionsContainerProps) {
  if (vm.menuSections.length === 0) {
    return null
  }

  return (
    <Actions>
      <JobActionsMenuItems sections={vm.menuSections} />
    </Actions>
  )
})
